'use client';

import { cn } from '../../lib/utils';
import { LegacyInteractiveEyes } from './LegacyInteractiveEyes';
import { LegacyRainaEarsInner } from './LegacyRainaEars';
import { LegacyRainaHornInner } from './LegacyRainaHorn';
import { eyebrowTransforms, eyePaths, headPaths, hoodiePaths } from './legacyPaths';
import { DEFAULT_EXPRESSION, type RainaExpression } from './useRainaAnimation';

const PE_AUTO = { pointerEvents: 'auto' } as const;

/** Eye area positioning within the full body viewBox */
const EYE_AREA_POSITION = { left: '26.87%', top: '35.17%', width: '46.03%', height: '15.74%' };

/** Bangs positioning within the full body viewBox */
const BANGS_POSITION = { left: '7.88%', top: '20.27%', width: '84%', height: '41.85%' };

/** Expression transition duration for smooth facial feature changes */
const EXPRESSION_TRANSITION_DURATION_MS = 300;

interface LegacyRainaHeadProps {
  svgId: string;
  eyeTracking: boolean;
  expression?: RainaExpression;
  isBlinking?: boolean;
  isNodding?: boolean;
  isHeadTilting?: boolean;
  headTiltDirection?: 'left' | 'right';
  isEarTwitching?: boolean;
  idleAnimations?: boolean;
  onNodEnd?: () => void;
  onHeadTiltEnd?: () => void;
  onEarTwitchEnd?: () => void;
  onBlinkEnd?: () => void;
  /** Whether to render ears. Set to false for badge variant where ears are rendered separately. Default: true */
  renderEars?: boolean;
  /** Whether to render horn. Set to false for badge variant where horn is rendered separately. Default: true */
  renderHorn?: boolean;
}

export function LegacyRainaHead({
  svgId,
  eyeTracking,
  expression = DEFAULT_EXPRESSION,
  isBlinking = false,
  isNodding = false,
  isHeadTilting = false,
  headTiltDirection = 'right',
  isEarTwitching = false,
  idleAnimations = true,
  onNodEnd,
  onHeadTiltEnd,
  onEarTwitchEnd,
  onBlinkEnd,
  renderEars = true,
  renderHorn = true,
}: LegacyRainaHeadProps) {
  const id = (name: string) => `${svgId}${name}`;

  // Full class names for Tailwind JIT detection (no dynamic interpolation)
  const headAnimationClass = isNodding
    ? 'motion-safe:animate-[raina-nod_0.7s_ease-in-out]'
    : isHeadTilting
      ? headTiltDirection === 'right'
        ? 'motion-safe:animate-[rainaHeadTiltRight_1s_ease-in-out]'
        : 'motion-safe:animate-[rainaHeadTiltLeft_1s_ease-in-out]'
      : '';

  const handleHeadAnimationEnd = () => {
    if (isNodding) onNodEnd?.();
    else if (isHeadTilting) onHeadTiltEnd?.();
  };

  return (
    <div className="absolute inset-0 pointer-events-none">
      <div
        className={cn('absolute inset-0 pointer-events-none', headAnimationClass)}
        style={{ transformOrigin: '50% 70%', willChange: 'transform' }}
        onAnimationEnd={handleHeadAnimationEnd}
      >
        {/* Ears */}
        {renderEars && <LegacyRainaEarsInner svgId={svgId} isEarTwitching={isEarTwitching} onEarTwitchEnd={onEarTwitchEnd} />}
        {/* Hood */}
        <div className="absolute inset-[9.6%_4.49%_39.37%_3.93%] pointer-events-none">
          <svg aria-hidden="true" className="block size-full" fill="none" preserveAspectRatio="none" viewBox="0 0 392.52 334.255">
            <defs>
              <linearGradient gradientUnits="userSpaceOnUse" id={id('hoodGrad')} x1="204.31" x2="261.041" y1="54.4315" y2="243.792">
                <stop stopColor="#BD98FF" />
                <stop offset="1" stopColor="#762BFA" />
              </linearGradient>
            </defs>
            <path d={hoodiePaths.hood} fill={`url(#${id('hoodGrad')})`} data-region="head" style={PE_AUTO} />
          </svg>
        </div>
        {/* Horn */}
        {renderHorn && <LegacyRainaHornInner svgId={svgId} />}
        {/* Ponytail */}
        <div
          className={cn(
            'absolute inset-[51.03%_9.83%_33.15%_62.81%] pointer-events-none',
            isNodding && 'motion-safe:animate-[rainaPonytailNod_0.7s_ease-out]',
            !isNodding &&
              isHeadTilting &&
              (headTiltDirection === 'right'
                ? 'motion-safe:animate-[rainaPonytailBounceLeft_1s_linear]'
                : 'motion-safe:animate-[rainaPonytailBounceRight_1s_linear]'),
            !isNodding && !isHeadTilting && idleAnimations && 'motion-safe:animate-[raina-ponytail-sway_4s_ease-in-out_infinite]',
          )}
          style={{ transformOrigin: 'left center', willChange: 'transform' }}
        >
          <svg aria-hidden="true" className="block size-full" fill="none" preserveAspectRatio="none" viewBox="0 0 117.246 103.601">
            <defs>
              <linearGradient gradientUnits="userSpaceOnUse" id={id('ponytailGrad1')} x1="117.155" x2="17.9993" y1="32.7107" y2="79.7235">
                <stop offset="0.135461" stopColor="#6EE66C" />
                <stop offset="0.386467" stopColor="#FFEB3E" />
                <stop offset="0.61441" stopColor="#FFAB43" />
                <stop offset="0.819356" stopColor="#F97DF3" />
                <stop offset="1" stopColor="#762BFA" />
              </linearGradient>
              <linearGradient gradientUnits="userSpaceOnUse" id={id('ponytailGrad2')} x1="132.02" x2="7.68348" y1="27.8625" y2="86.814">
                <stop offset="0.135461" stopColor="#6EE66C" />
                <stop offset="0.386467" stopColor="#FFEB3E" />
                <stop offset="0.61441" stopColor="#FFAB43" />
                <stop offset="0.819356" stopColor="#F97DF3" />
                <stop offset="1" stopColor="#762BFA" />
              </linearGradient>
              <mask id={id('ponytailMask')} maskUnits="userSpaceOnUse" style={{ maskType: 'alpha' }}>
                <path d={headPaths.ponytailMask} fill="black" />
                <path d={headPaths.ponytailMask} fill={`url(#${id('ponytailGrad1')})`} />
              </mask>
              <filter
                colorInterpolationFilters="sRGB"
                filterUnits="userSpaceOnUse"
                height="157.372"
                id={id('ponytailBlur')}
                width="174.669"
                x="-28.6447"
                y="-26.9636"
              >
                <feFlood floodOpacity="0" result="BackgroundImageFix" />
                <feBlend in="SourceGraphic" in2="BackgroundImageFix" mode="normal" result="shape" />
                <feGaussianBlur result="effect1_foregroundBlur" stdDeviation="6.95" />
              </filter>
            </defs>
            <g mask={`url(#${id('ponytailMask')})`}>
              <g filter={`url(#${id('ponytailBlur')})`}>
                <path d={headPaths.ponytailColor} fill="black" />
                <path d={headPaths.ponytailColor} fill={`url(#${id('ponytailGrad2')})`} data-region="head" style={PE_AUTO} />
              </g>
            </g>
          </svg>
        </div>
        {/* Face */}
        <div className="absolute inset-[20.25%_8.24%_37.97%_7.87%] pointer-events-none">
          <svg aria-hidden="true" className="block size-full" fill="none" preserveAspectRatio="none" viewBox="0 0 359.554 273.691">
            <defs>
              <filter
                colorInterpolationFilters="sRGB"
                filterUnits="userSpaceOnUse"
                height="288.257"
                id={id('faceFilter')}
                width="359.554"
                x="0"
                y="-14.5662"
              >
                <feFlood floodOpacity="0" result="BackgroundImageFix" />
                <feBlend in="SourceGraphic" in2="BackgroundImageFix" mode="normal" result="shape" />
                <feColorMatrix in="SourceAlpha" result="hardAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" />
                <feOffset dy="-14.5662" />
                <feGaussianBlur stdDeviation="13.7" />
                <feComposite in2="hardAlpha" k2="-1" k3="1" operator="arithmetic" />
                <feColorMatrix type="matrix" values="0 0 0 0 0.79817 0 0 0 0 0.490196 0 0 0 0 0.976471 0 0 0 1 0" />
                <feBlend in2="shape" mode="normal" result="effect1_innerShadow" />
              </filter>
            </defs>
            <g filter={`url(#${id('faceFilter')})`}>
              <path d={headPaths.face} fill="#F6C8BB" data-region="head" style={PE_AUTO} />
            </g>
          </svg>
        </div>
        {/* Eyebrows (rendered before bangs so hair overlaps naturally) */}
        <div className="absolute overflow-visible pointer-events-none" style={EYE_AREA_POSITION}>
          <div className="absolute inset-[-3.38%_0%] overflow-visible">
            <svg
              aria-hidden="true"
              className="block size-full overflow-visible"
              fill="none"
              preserveAspectRatio="none"
              viewBox="0 0 197 103.567"
            >
              {/* Right Eyebrow */}
              <g
                style={{
                  transformOrigin: '164px 10px',
                  transform: `rotate(${eyebrowTransforms[expression]?.right.rotate || 0}deg) translateY(${eyebrowTransforms[expression]?.right.translateY || 0}px)`,
                  transition: `transform ${EXPRESSION_TRANSITION_DURATION_MS}ms ease-out`,
                }}
              >
                <path d={eyePaths.rightEyebrow} stroke="#33116F" strokeLinecap="round" strokeWidth="6.55515" />
              </g>
              {/* Left Eyebrow */}
              <g
                style={{
                  transformOrigin: '32px 10px',
                  transform: `rotate(${eyebrowTransforms[expression]?.left.rotate || 0}deg) translateY(${eyebrowTransforms[expression]?.left.translateY || 0}px)`,
                  transition: `transform ${EXPRESSION_TRANSITION_DURATION_MS}ms ease-out`,
                }}
              >
                <path d={eyePaths.leftEyebrow} stroke="#33116F" strokeLinecap="round" strokeWidth="6.55515" />
              </g>
            </svg>
          </div>
        </div>
        {/* Bangs */}
        <div className="absolute pointer-events-none" style={BANGS_POSITION}>
          <svg aria-hidden="true" className="block size-full" fill="none" preserveAspectRatio="none" viewBox="0 0 359.554 273.691">
            <defs>
              <linearGradient gradientUnits="userSpaceOnUse" id={id('bangsGrad')} x1="14.3139" x2="369.156" y1="54.4316" y2="272.948">
                <stop offset="0.0356819" stopColor="#762BFA" />
                <stop offset="0.246029" stopColor="#F97DF3" />
                <stop offset="0.434543" stopColor="#FFAB43" />
                <stop offset="0.681265" stopColor="#FFE928" />
                <stop offset="1" stopColor="#6EE66C" />
              </linearGradient>
              <mask id={id('bangsFaceMask')} maskUnits="userSpaceOnUse" style={{ maskType: 'alpha' }}>
                <path d={headPaths.face} fill="#F6C8BB" />
              </mask>
              <mask id={id('bangsShapeMask')} maskUnits="userSpaceOnUse" style={{ maskType: 'alpha' }}>
                <path d={headPaths.bangsShape} fill="#D9D9D9" />
              </mask>
              <filter
                colorInterpolationFilters="sRGB"
                filterUnits="userSpaceOnUse"
                height="604.522"
                id={id('bangsBlur')}
                width="589.189"
                x="-90.7339"
                y="-256.448"
              >
                <feFlood floodOpacity="0" result="BackgroundImageFix" />
                <feBlend in="SourceGraphic" in2="BackgroundImageFix" mode="normal" result="shape" />
                <feGaussianBlur result="effect1_foregroundBlur" stdDeviation="21.8" />
              </filter>
            </defs>
            <g mask={`url(#${id('bangsFaceMask')})`}>
              <g mask={`url(#${id('bangsShapeMask')})`}>
                <g filter={`url(#${id('bangsBlur')})`}>
                  <rect fill="#D9D9D9" height="517.227" width="501.894" x="-47.0865" y="-212.801" />
                  <rect fill={`url(#${id('bangsGrad')})`} height="517.227" width="501.894" x="-47.0865" y="-212.801" />
                </g>
              </g>
            </g>
          </svg>
        </div>
        {/* Eyes */}
        <LegacyInteractiveEyes
          svgId={svgId}
          enabled={eyeTracking}
          expression={expression}
          isBlinking={isBlinking}
          idleAnimations={idleAnimations}
          onBlinkEnd={onBlinkEnd}
        />
      </div>
    </div>
  );
}
